import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { useSelector } from 'react-redux'
import {
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import {
  BookmarkBorder as BookmarkIcon,
  CampaignOutlined as WhatsNewIcon,
  InsertChartOutlined as MyLearningIcon,
  PersonOutline as ProfileIcon,
  ExitToApp as LogoutIcon
} from '@mui/icons-material'
import { useTenant } from '@emerald-works-nova/auth'
import useLogout from '../../../hooks/useLogout'
import { routes } from '../../../router/routes'
import { userSlice } from '../../../reducers'
import { useAlert, useFormFactor } from '../../../hooks'
import ActionSheet from '../../../components/action-sheet'
import ConfirmDialog from '../../../components/confirm-dialog'
import {
  WHATS_NEW_COPY,
  WHATS_NEW_ENABLED,
  WHATS_NEW_ENTRY_SOURCE,
  isFresh,
  whatsNewPath
} from '../../whats-new/constants'
import { useAnnouncements } from '../../whats-new/hooks/useAnnouncements'
import { NewChip } from '../../whats-new/new-chip'

const useStyles = makeStyles(({ spacing, shadows, palette, typography, radius }) => ({
  profileMenu: {
    '& .MuiPaper-root': {
      borderRadius: spacing(1),
      marginTop: spacing(1),
      minWidth: 200,
      boxShadow: shadows[8]
    }
  },
  navButton: {
    textTransform: 'none',
    borderRadius: spacing(1),
    padding: `${spacing(1)} ${spacing(2)}`,
    color: palette.text.primary,
    fontWeight: typography.fontWeightMedium,
    minHeight: 40
  },
  newChip: {
    marginLeft: spacing(1.5)
  },
  // The phone rendering (see ActionSheet) lists the same actions as rows.
  sheetItem: {
    minHeight: spacing(7),
    paddingLeft: spacing(3),
    paddingRight: spacing(3),
    color: palette.text.primary,
    '& .MuiListItemText-primary': {
      fontWeight: typography.fontWeightMedium
    }
  }
}))

export const PaidConsumerProfileMenu = ({ anchorEl, open, onClose }) => {
  const classes = useStyles()
  const { isMobile } = useFormFactor()
  const profile = useSelector(userSlice.selectors.selectProfile)
  const { logout } = useLogout()
  const { enqueueMsg } = useAlert()
  const tenant = useTenant()
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)
  const showMyLearning = tenant.features?.myLearning !== false
  // "New" next to What's new while the latest note is fresh (zero persistence).
  const { latestPublishedAt } = useAnnouncements({ eager: WHATS_NEW_ENABLED })
  const showWhatsNewChip = WHATS_NEW_ENABLED && isFresh(latestPublishedAt)

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true)
    onClose()
  }

  const handleLogout = async () => {
    try {
      await logout(routes.login.path)
    } catch (error) {
      enqueueMsg('Unable to logout', 'error')
    }
  }

  // One list for both renderings, so the actions and their test ids cannot
  // drift between the popover and the sheet.
  const items = [
    showMyLearning && {
      label: 'My Learning',
      Icon: MyLearningIcon,
      to: routes.myLearning.path,
      dataTest: 'paid-consumer-profile-menu-my-learning'
    },
    {
      label: 'Bookmarks',
      Icon: BookmarkIcon,
      to: routes.bookmarks.path,
      dataTest: 'paid-consumer-profile-menu-bookmarks'
    },
    {
      label: 'My account',
      Icon: ProfileIcon,
      to: routes.profile.path,
      dataTest: 'paid-consumer-profile-menu-my-profile'
    },
    WHATS_NEW_ENABLED && {
      label: WHATS_NEW_COPY.title,
      Icon: WhatsNewIcon,
      to: whatsNewPath(WHATS_NEW_ENTRY_SOURCE.MENU),
      dataTest: 'paid-consumer-profile-menu-whats-new',
      showNewChip: showWhatsNewChip
    },
    {
      label: 'Sign Out',
      Icon: LogoutIcon,
      onClick: handleLogoutClick,
      dataTest: 'paid-consumer-profile-menu-logout',
      dividerBefore: true
    }
  ].filter(Boolean)

  // The sheet's subtle heading says whose account this is; the popover sits
  // under the account icon and needs none.
  const displayName = [profile?.givenName, profile?.familyName].filter(Boolean).join(' ') || profile?.email
  const sheetTitle = displayName ? `Signed in as ${displayName}` : 'Account'

  // Links close the menu on their way out; Sign Out closes it itself before
  // asking for confirmation.
  const actionProps = ({ to, onClick }) => (to ? { component: Link, to, onClick: onClose } : { onClick })

  const confirmDialog = (
    <ConfirmDialog
      name='confirm-logout'
      title='Are you sure you want to logout?'
      open={showLogoutConfirm}
      onClose={() => setShowLogoutConfirm(false)}
      onConfirm={handleLogout}
    />
  )

  if (isMobile) {
    return (
      <>
        <ActionSheet
          open={open}
          onClose={onClose}
          title={sheetTitle}
          dataTest='paid-consumer-profile-sheet'
        >
          <List component='nav' aria-label='Profile menu' disablePadding>
            {items.map((item) => (
              <React.Fragment key={item.dataTest}>
                {item.dividerBefore && <Divider />}
                <ListItemButton
                  {...actionProps(item)}
                  className={classes.sheetItem}
                  data-test={item.dataTest}
                >
                  <ListItemIcon>
                    <item.Icon />
                  </ListItemIcon>
                  <ListItemText primary={item.label} />
                  {item.showNewChip && <NewChip dataTest={`${item.dataTest}-chip`} className={classes.newChip} />}
                </ListItemButton>
              </React.Fragment>
            ))}
          </List>
        </ActionSheet>
        {confirmDialog}
      </>
    )
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        className={classes.profileMenu}
        disableScrollLock
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {/* Menu rejects Fragment children, so the divider is its own entry. */}
        {items.flatMap((item) => [
          item.dividerBefore && <Divider key={`${item.dataTest}-divider`} />,
          <MenuItem
            key={item.dataTest}
            {...actionProps(item)}
            className={classes.navButton}
            data-test={item.dataTest}
          >
            <ListItemIcon>
              <item.Icon />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
            {item.showNewChip && <NewChip dataTest={`${item.dataTest}-chip`} className={classes.newChip} />}
          </MenuItem>
        ]).filter(Boolean)}
      </Menu>
      {confirmDialog}
    </>
  )
}
